import type { AssetType } from '../enums/AssetType'
import type { AssetGroup } from './AssetGroup'
import type { Aurora } from './Aurora'
import { AxiomaW1 } from './AxiomaW1'
import type Company from './Company'
import type Device from './Device'
import type Level from './Level'
import type Lift from './Lift'
import type Project from './Project'
import type RuuviTag from './RuuviTag'
import { Upload } from './Upload'

export enum AssetStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export type Asset = {
  id: string
  type: AssetType
  name: string
  description: string | null
  status: AssetStatus
  group?: AssetGroup | null
  groupId: string | null
  company?: Company | null
  companyId: number | null
  project?: Project | null
  projectId: number | null
  level?: Level | null
  levelId: number | null
  lift?: Lift | null
  liftId: number | null
  device?: Device | null
  deviceName: string | null
  ruuviTag?: RuuviTag | null
  ruuviTagId: string | null
  aurora?: Aurora | null
  auroraId: string | null
  axiomaW1?: AxiomaW1 | null
  axiomaW1Id: string | null
  photo?: Upload | null
  photoId: string | null
  photoUrl?: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  attachedAt?: string | null
  detachedAt?: string | null
  /** Heating topology: the asset feeding this one (heat source or t_junction) */
  heatingParentId?: string | null
  /** Heating topology: nominalDiameter of the pipe from the heating parent */
  customData?: {
    nominalDiameter?: number
  } | null
}

export interface DeviceAsset<T extends Device = Device> extends Asset {
  type: AssetType.DEVICE
  device: T
  deviceId: string
  auroraId: never
  aurora: never
  ruuviTagId: never
  ruuviTag: never
}

export interface RuuviTagAsset extends Asset {
  type: AssetType.RUUVI_TAG
  ruuviTag: RuuviTag
  ruuviTagId: string
  auroraId: never
  aurora: never
  deviceId: never
  device: never
}

export interface AuroraAsset extends Asset {
  type: AssetType.AURORA
  aurora: Aurora
  auroraId: string
  deviceId: never
  device: never
  ruuviTagId: never
  ruuviTag: never
}

export interface AxiomaW1Asset extends Asset {
  type: AssetType.AXIOMA_W1
  axiomaW1: AxiomaW1
  axiomaW1Id: string
}

export interface Assetable {
  asset?: Asset | null
}

export type WithAsset<T extends Assetable> = T & {
  asset: Asset
}

export type Installable<T extends Assetable> = WithAsset<T>

export type Uninstallable<T extends Assetable> = WithAsset<T>

export default Asset
